import type { FC, ReactNode } from 'react';

import Avatar from '@/common-components/Avatar';
import ListingStatusPill from '@/common-components/ListingStatusPill';
import MediaGallery from '@/common-components/MediaGallery';
import ReportsList from '@/common-components/ReportsList';
import { CATEGORY_META } from '@/constants/listing.constants';
import type { UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';
import { formatRupees } from '@/utils/currency.util';
import { formatDate } from '@/utils/date.util';
import type { AdminListingDetail, ListingCategory, ListingStatus } from '@/types/rental.types';

interface ListingDetailPanelProps {
  /** Known before the fetch resolves, so the header can render immediately. */
  title: string;
  category?: ListingCategory;
  status?: ListingStatus;
  campusName?: string;
  detail: AdminListingDetail | undefined;
  gallery: UseMediaGalleryResult;
  isLoading: boolean;
  isError: boolean;
  /** Rendered above the sections — the AI verdict when opened from the AI Flagged queue. */
  banner?: ReactNode;
}

const InfoRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[var(--color-divider)] py-2.5 last:border-b-0">
    <span className="shrink-0 text-[12.5px] font-medium text-[var(--color-ink-muted)]">
      {label}
    </span>
    <span className="text-right text-[13px] text-[var(--color-ink)]">{value}</span>
  </div>
);

const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-ink-soft)]">
    {children}
  </p>
);

const Divider: FC = () => <div className="mx-5 border-t border-[var(--color-divider)]" />;

/**
 * Everything about one listing, with no drawer chrome or actions — Rental Management and the
 * Moderation Center both show this and only differ in what the footer buttons do.
 */
const ListingDetailPanel: FC<ListingDetailPanelProps> = ({
  title,
  category,
  status,
  campusName,
  detail,
  gallery,
  isLoading,
  isError,
  banner,
}) => {
  const meta = category ? CATEGORY_META[category] : detail ? CATEGORY_META[detail.category] : null;
  const shownStatus = detail?.status ?? status;

  return (
    <>
      <MediaGallery
        gallery={gallery}
        alt={title}
        emptyIcon={meta?.icon}
        emptyBackground={meta?.gradient}
      />

      <div className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-[17px] font-bold text-[var(--color-ink)]">{title}</div>
            <div className="mt-0.5 text-[12px] text-[var(--color-ink-soft)]">
              {meta?.label ?? '—'} · {detail?.campusName ?? campusName ?? '—'}
            </div>
          </div>
          {shownStatus ? <ListingStatusPill status={shownStatus} /> : null}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {banner}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-ink-muted)]">
            Loading details…
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-danger-ink)]">
            Failed to load details. Try closing and reopening.
          </div>
        ) : detail ? (
          <>
            <div className="px-5 py-4">
              <SectionHeading>Pricing</SectionHeading>
              <InfoRow
                label="Price per day"
                value={
                  <span className="font-bold text-brand-500">
                    {formatRupees(detail.pricePerDayPaise)}
                  </span>
                }
              />
              <InfoRow
                label="Security deposit"
                value={detail.deposit.enabled ? formatRupees(detail.deposit.amountPaise) : 'None'}
              />
            </div>

            <Divider />

            <div className="px-5 py-4">
              <SectionHeading>Owner</SectionHeading>
              <div className="flex items-center gap-3 py-2">
                <Avatar name={detail.owner.name} src={detail.owner.avatarUrl} size="md" />
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-[var(--color-ink)]">
                    {detail.owner.name}
                  </div>
                  <div className="text-[11.5px] text-[var(--color-ink-muted)]">
                    {[detail.owner.year, detail.owner.stream].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <div className="ml-auto text-right text-[11.5px] text-[var(--color-ink-muted)]">
                  <div>Trust {detail.owner.trustScore}</div>
                  {detail.owner.ratingAvg > 0 ? <div>★ {detail.owner.ratingAvg.toFixed(1)}</div> : null}
                </div>
              </div>
            </div>

            <Divider />

            <div className="px-5 py-4">
              <SectionHeading>Performance</SectionHeading>
              <InfoRow label="Times Rented" value={detail.timesRented} />
              <InfoRow label="Active Rentals Now" value={detail.activeRentalCount} />
              <InfoRow
                label="Rating"
                value={
                  detail.ratingCount > 0
                    ? `★ ${detail.ratingAvg.toFixed(1)} (${detail.ratingCount} review${detail.ratingCount === 1 ? '' : 's'})`
                    : 'No ratings yet'
                }
              />
            </div>

            {detail.description ? (
              <>
                <Divider />
                <div className="px-5 py-4">
                  <SectionHeading>Description</SectionHeading>
                  <p className="py-1 text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
                    {detail.description}
                  </p>
                </div>
              </>
            ) : null}

            <Divider />

            <div className="px-5 py-4">
              <SectionHeading>
                Reports{detail.reportCount > 0 ? ` (${detail.reportCount})` : ''}
              </SectionHeading>
              <ReportsList reports={detail.reports} totalCount={detail.reportCount} />
            </div>

            {detail.recentReviews.length > 0 ? (
              <>
                <Divider />
                <div className="px-5 py-4">
                  <SectionHeading>Recent Reviews</SectionHeading>
                  <div className="flex flex-col gap-2.5 pt-1">
                    {detail.recentReviews.map((review) => (
                      <div
                        key={review.id}
                        className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-3"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] font-semibold text-[var(--color-ink)]">
                            {review.authorName}
                          </span>
                          <span className="text-[11px] text-[var(--color-warn-ink)]">
                            {'★'.repeat(Math.round(review.rating))}
                          </span>
                        </div>
                        {review.comment ? (
                          <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">
                            “{review.comment}”
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            <Divider />

            <div className="px-5 py-4">
              <SectionHeading>Listing</SectionHeading>
              <InfoRow label="Created" value={formatDate(detail.createdAt)} />
              <InfoRow label="Last Updated" value={formatDate(detail.updatedAt)} />
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

export default ListingDetailPanel;
